/**
 * Agent Hub — Skills Tab
 *
 * Skill management scoped to this chatbot.
 * Extracted from Skills.tsx page logic (no chatbot selector needed).
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Modal } from '../../../components/ui/Modal'
import { SkillBuilder } from '../../../components/chatbot/SkillBuilder'
import { skillsService, type SkillItem, type SkillAnalytics, type SkillFullResponse, type SkillLogsResponse } from '../../../services/skills'
import { useToast } from '../../../hooks/useToast'
import { logger } from '../../../utils/logger'
import type { Chatbot } from '../../../types'
import {
  Plus, Upload, Loader2, Pencil, Settings, Trash2,
  ToggleLeft, ToggleRight, Shield, Tag, X,
  Briefcase, Wrench, MessageCircle, ShoppingCart, Zap, BarChart3, Clock, Activity,
  CheckCircle, AlertTriangle, Timer, TrendingUp, TrendingDown, ArrowRight, Filter,
} from 'lucide-react'

interface SkillsTabProps {
  chatbot: Chatbot
}

export const SkillsTab: React.FC<SkillsTabProps> = ({ chatbot }) => {
  const [botSkills, setBotSkills] = useState<SkillItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [showConfigModal, setShowConfigModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showBuilder, setShowBuilder] = useState(false)
  const [builderSkillId, setBuilderSkillId] = useState<string | null>(null)
  const [configSkill, setConfigSkill] = useState<SkillItem | null>(null)
  const [configCredentials, setConfigCredentials] = useState<Record<string, string>>({})
  const [configSettings, setConfigSettings] = useState<Record<string, any>>({})
  const [isToggling, setIsToggling] = useState<string | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [analytics, setAnalytics] = useState<SkillAnalytics | null>(null)
  const [editSkillData, setEditSkillData] = useState<SkillFullResponse | null>(null)
  const [detailSkill, setDetailSkill] = useState<SkillItem | null>(null)
  const [detailLogs, setDetailLogs] = useState<SkillLogsResponse | null>(null)
  const [isLoadingLogs, setIsLoadingLogs] = useState(false)
  const [logFilter, setLogFilter] = useState<'all' | 'success' | 'error' | 'timeout'>('all')
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null)

  const { toast } = useToast()

  const loadSkills = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [forBot, analyticsData] = await Promise.all([
        skillsService.listForBot(chatbot.id),
        skillsService.getAnalytics(chatbot.id).catch(() => null),
      ])
      setBotSkills(forBot)
      setAnalytics(analyticsData)
    } catch (err: any) {
      setError(err.message || 'Failed to load skills')
      logger.error('Failed to load skills', err)
    } finally {
      setIsLoading(false)
    }
  }, [chatbot.id])

  useEffect(() => { loadSkills() }, [loadSkills])

  const handleToggle = async (skill: SkillItem) => {
    setIsToggling(skill.id)
    try {
      if (skill.enabled) {
        await skillsService.disable(chatbot.id, skill.id)
        toast({ title: `${skill.name} disabled`, variant: 'default' })
      } else {
        if (skill.has_credentials && !skill.credentials_configured) {
          setConfigSkill(skill)
          setConfigCredentials({})
          setConfigSettings(skill.config || {})
          setShowConfigModal(true)
          setIsToggling(null)
          return
        }
        await skillsService.enable(chatbot.id, skill.id)
        toast({ title: `${skill.name} enabled`, variant: 'default' })
      }
      await loadSkills()
    } catch (err: any) {
      toast({ title: `Failed: ${err.message}`, variant: 'destructive' })
    } finally {
      setIsToggling(null)
    }
  }

  const handleSaveConfig = async () => {
    if (!configSkill) return
    try {
      await skillsService.enable(chatbot.id, configSkill.id, { credentials: configCredentials, config: configSettings })
      toast({ title: `${configSkill.name} configured and enabled`, variant: 'default' })
      setShowConfigModal(false)
      setConfigSkill(null)
      await loadSkills()
    } catch (err: any) {
      toast({ title: `Failed: ${err.message}`, variant: 'destructive' })
    }
  }

  const handleUpload = async () => {
    if (!uploadFile) return
    setIsUploading(true)
    try {
      const skill = await skillsService.upload(uploadFile)
      toast({ title: `Skill "${skill.name}" uploaded`, variant: 'default' })
      setShowUploadModal(false)
      setUploadFile(null)
      await loadSkills()
    } catch (err: any) {
      toast({ title: `Upload failed: ${err.response?.data?.detail || err.message}`, variant: 'destructive' })
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = async (skill: SkillItem) => {
    if (skill.visibility === 'builtin') return
    if (!confirm(`Delete skill "${skill.name}"?`)) return
    try {
      await skillsService.delete(skill.id)
      toast({ title: `${skill.name} deleted`, variant: 'default' })
      await loadSkills()
    } catch (err: any) {
      toast({ title: `Failed: ${err.message}`, variant: 'destructive' })
    }
  }

  // Pre-fetch skill data, then open builder
  const handleEditSkill = async (skillId: string) => {
    try {
      const data = await skillsService.getFullSkill(skillId)
      setEditSkillData(data)
      setBuilderSkillId(skillId)
      setShowBuilder(true)
    } catch (err: any) {
      toast({ title: `Failed to load skill: ${err.message}`, variant: 'destructive' })
    }
  }

  const handleCreateSkill = () => {
    setEditSkillData(null)
    setBuilderSkillId(null)
    setShowBuilder(true)
  }

  const handleOpenDetail = async (skill: SkillItem) => {
    setDetailSkill(skill)
    setDetailLogs(null)
    setLogFilter('all')
    setIsLoadingLogs(true)
    try {
      const logs = await skillsService.getLogs(chatbot.id, skill.name, 50)
      setDetailLogs(logs)
    } catch (err: any) {
      logger.error('Failed to load skill logs', err)
    } finally {
      setIsLoadingLogs(false)
    }
  }

  const filteredLogs = React.useMemo(() => {
    if (!detailLogs) return []
    if (logFilter === 'all') return detailLogs.logs
    return detailLogs.logs.filter(l => l.status === logFilter)
  }, [detailLogs, logFilter])

  const formatTimestamp = (iso: string): string => {
    const d = new Date(iso)
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  const formatLatency = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  const builtinSkills = botSkills.filter(s => s.visibility === 'builtin')
  const customSkills = botSkills.filter(s => s.visibility !== 'builtin')

  // Build a map from skill name -> analytics entry (backend already maps tool→skill)
  const analyticsMap = React.useMemo(() => {
    const map: Record<string, { executions: number; last_used: string }> = {}
    if (analytics?.skills) {
      for (const s of analytics.skills) {
        map[s.name] = { executions: s.executions, last_used: s.last_used }
      }
    }
    return map
  }, [analytics])

  const getSkillExecCount = (skill: SkillItem): number => {
    // Backend maps tool_calls → skill names, so direct match works
    if (analyticsMap[skill.name]) return analyticsMap[skill.name].executions
    // Also check by tool names as fallback
    let total = 0
    if (skill.tools) {
      for (const tool of skill.tools) {
        const toolName = typeof tool === 'string' ? tool : tool.name
        if (toolName && analyticsMap[toolName]) total += analyticsMap[toolName].executions
      }
    }
    return total
  }

  const formatLastUsed = (iso: string): string => {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    return `${days}d ago`
  }

  const CategoryIcon: React.FC<{ category?: string; className?: string }> = ({ category, className = 'w-4 h-4' }) => {
    switch (category) {
      case 'sales': return <Briefcase className={className} />
      case 'utility': return <Wrench className={className} />
      case 'engagement': return <MessageCircle className={className} />
      case 'ecommerce': return <ShoppingCart className={className} />
      default: return <Zap className={className} />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Add capabilities to this agent with skills</p>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreateSkill}><Plus className="w-4 h-4 mr-2" />Create Skill</Button>
          <Button variant="outline" onClick={() => setShowUploadModal(true)}><Upload className="w-4 h-4 mr-2" />Upload</Button>
        </div>
      </div>

      {/* Analytics bar */}
      {analytics && analytics.total_executions > 0 && (
        <div id="skill-analytics-bar" className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="py-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Activity className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{analytics.total_executions}</p>
                  <p className="text-xs text-muted-foreground">Executions (last {analytics.period_days}d)</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{analytics.skills.length}</p>
                  <p className="text-xs text-muted-foreground">Active Skills</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {analytics.skills.length > 0 ? formatLastUsed(analytics.skills[0].last_used) : 'N/A'}
                  </p>
                  <p className="text-xs text-muted-foreground">Last Used</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {isLoading && <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>}
      {error && <Card className="border-destructive/30 bg-destructive/5"><CardContent className="py-4 text-destructive">{error}</CardContent></Card>}

      {!isLoading && !error && (
        <>
          {/* Built-in */}
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2"><Shield className="w-5 h-5 text-blue-500" />Built-in Skills</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {builtinSkills.map(skill => (
                <Card key={skill.id} className={`transition-all ${skill.enabled ? 'ring-2 ring-purple-300 dark:ring-purple-700' : ''}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2 cursor-pointer hover:text-purple-600 transition-colors" onClick={() => handleOpenDetail(skill)}><CategoryIcon category={skill.category} />{skill.name}</CardTitle>
                      <div className="flex items-center gap-1">
                        {getSkillExecCount(skill) > 0 && (
                          <button
                            onClick={() => handleOpenDetail(skill)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs font-semibold hover:bg-purple-500/20 transition-colors"
                            title={`${getSkillExecCount(skill)} executions — click for details`}
                          >
                            <Activity className="w-3 h-3" />
                            {getSkillExecCount(skill)}
                          </button>
                        )}
                        <button onClick={() => handleToggle(skill)} disabled={isToggling === skill.id} className="text-muted-foreground hover:text-purple-600 transition-colors">
                          {isToggling === skill.id ? <Loader2 className="w-5 h-5 animate-spin" /> : skill.enabled ? <ToggleRight className="w-6 h-6 text-purple-600" /> : <ToggleLeft className="w-6 h-6" />}
                        </button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2">{skill.description}</p>
                    {skill.tools && skill.tools.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {skill.tools.map((tool: any, idx: number) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-medium" title={tool.description || tool.name}>
                            <Wrench className="w-3 h-3" />
                            {tool.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {skill.tags && skill.tags.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {skill.tags.map((tag: string, idx: number) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px]">
                            <Tag className="w-2.5 h-2.5" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">v{skill.version}</span>
                      {skill.enabled && skill.has_credentials && (
                        <Button variant="ghost" size="sm" onClick={() => { setConfigSkill(skill); setConfigCredentials({}); setConfigSettings(skill.config || {}); setShowConfigModal(true) }}>
                          <Settings className="w-4 h-4 mr-1" />Configure
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Custom */}
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2"><Plus className="w-5 h-5 text-green-500" />My Skills</h2>
            {customSkills.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground">No custom skills yet.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customSkills.map(skill => (
                  <Card key={skill.id} className={`transition-all ${skill.enabled ? 'ring-2 ring-green-300 dark:ring-green-700' : ''}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2 cursor-pointer hover:text-green-600 transition-colors" onClick={() => handleOpenDetail(skill)}><CategoryIcon category={skill.category} />{skill.name}</CardTitle>
                        <div className="flex items-center gap-1">
                          {getSkillExecCount(skill) > 0 && (
                            <button
                              onClick={() => handleOpenDetail(skill)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-semibold hover:bg-green-500/20 transition-colors"
                              title={`${getSkillExecCount(skill)} executions — click for details`}
                            >
                              <Activity className="w-3 h-3" />
                              {getSkillExecCount(skill)}
                            </button>
                          )}
                          <button onClick={() => handleToggle(skill)} disabled={isToggling === skill.id} className="text-muted-foreground hover:text-green-600 transition-colors">
                            {isToggling === skill.id ? <Loader2 className="w-5 h-5 animate-spin" /> : skill.enabled ? <ToggleRight className="w-6 h-6 text-green-600" /> : <ToggleLeft className="w-6 h-6" />}
                          </button>
                          <button onClick={() => handleEditSkill(skill.id)} className="text-muted-foreground hover:text-blue-500 ml-1"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(skill)} className="text-muted-foreground hover:text-red-500 ml-1"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2">{skill.description}</p>
                      {skill.tools && skill.tools.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {skill.tools.map((tool: any, idx: number) => (
                            <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-medium" title={tool.description || tool.name}>
                              <Wrench className="w-3 h-3" />
                              {tool.name}
                            </span>
                          ))}
                        </div>
                      )}
                      {skill.tags && skill.tags.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {skill.tags.map((tag: string, idx: number) => (
                            <span key={idx} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px]">
                              <Tag className="w-2.5 h-2.5" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">v{skill.version}</span>
                        {skill.enabled && skill.has_credentials && (
                          <Button variant="ghost" size="sm" onClick={() => { setConfigSkill(skill); setConfigCredentials({}); setConfigSettings(skill.config || {}); setShowConfigModal(true) }}>
                            <Settings className="w-4 h-4 mr-1" />Configure
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Skill Detail / Logs Panel */}
      {detailSkill && (
        <Modal isOpen={!!detailSkill} onClose={() => { setDetailSkill(null); setDetailLogs(null) }} size="xl" showCloseButton={false}>
          <div className="flex flex-col gap-6 py-2">
            {/* Skill header */}
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex-shrink-0">
                <CategoryIcon category={detailSkill.category} className="w-7 h-7 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2 className="text-lg font-semibold text-foreground tracking-tight">{detailSkill.name}</h2>
                  {detailSkill.version && (
                    <span className="text-[11px] text-muted-foreground px-2 py-0.5 rounded-md bg-muted font-medium">v{detailSkill.version}</span>
                  )}
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                    detailSkill.enabled
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${detailSkill.enabled ? 'bg-emerald-500' : 'bg-muted-foreground/50'}`} />
                    {detailSkill.enabled ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{detailSkill.description}</p>
              </div>
              <button
                onClick={() => { setDetailSkill(null); setDetailLogs(null) }}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Loading state */}
            {isLoadingLogs && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 className="w-7 h-7 animate-spin text-purple-500" />
                <p className="text-sm text-muted-foreground">Loading execution logs...</p>
              </div>
            )}

            {/* Content */}
            {detailLogs && (
              <div className="flex flex-col gap-5 overflow-y-auto flex-1 min-h-0" style={{ maxHeight: 'calc(80vh - 140px)' }}>
                {/* Stat cards row */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="rounded-xl border border-border bg-card/50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Runs</span>
                      <div className="p-1.5 rounded-lg bg-blue-500/10">
                        <Activity className="w-3.5 h-3.5 text-blue-500" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{detailLogs.summary.total_runs}</p>
                  </div>

                  <div className="rounded-xl border border-border bg-card/50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Success Rate</span>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${
                        detailLogs.summary.error_rate_pct <= 5
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                          : detailLogs.summary.error_rate_pct <= 20
                          ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                          : 'bg-red-500/10 text-red-600 dark:text-red-400'
                      }`}>
                        {detailLogs.summary.error_rate_pct <= 5 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {(100 - detailLogs.summary.error_rate_pct).toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">
                      {detailLogs.summary.total_runs - detailLogs.summary.error_count}
                      <span className="text-sm font-normal text-muted-foreground ml-1">/ {detailLogs.summary.total_runs}</span>
                    </p>
                  </div>

                  <div className="rounded-xl border border-border bg-card/50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Errors</span>
                      <div className="p-1.5 rounded-lg bg-red-500/10">
                        <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-foreground">
                      {detailLogs.summary.error_count}
                      {detailLogs.summary.error_count > 0 && (
                        <span className="text-xs font-medium text-red-500 ml-2">{detailLogs.summary.error_rate_pct.toFixed(1)}%</span>
                      )}
                    </p>
                  </div>

                  <div className="rounded-xl border border-border bg-card/50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Avg Latency</span>
                      <div className="p-1.5 rounded-lg bg-purple-500/10">
                        <Clock className="w-3.5 h-3.5 text-purple-500" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-foreground">
                      {detailLogs.summary.avg_latency_ms != null ? (
                        <>{detailLogs.summary.avg_latency_ms < 1000
                            ? <>{detailLogs.summary.avg_latency_ms}<span className="text-sm font-normal text-muted-foreground ml-0.5">ms</span></>
                            : <>{(detailLogs.summary.avg_latency_ms / 1000).toFixed(1)}<span className="text-sm font-normal text-muted-foreground ml-0.5">s</span></>
                        }</>
                      ) : <span className="text-muted-foreground">N/A</span>}
                    </p>
                  </div>
                </div>

                {/* Last error alert */}
                {detailLogs.summary.last_error && (
                  <div className="flex items-start gap-3 p-3.5 rounded-xl border border-red-500/20 bg-red-500/5">
                    <div className="p-1.5 rounded-lg bg-red-500/10 flex-shrink-0 mt-0.5">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-red-600 dark:text-red-400">Last Error</p>
                      <p className="text-xs text-red-500/80 dark:text-red-400/70 mt-1 break-words line-clamp-2">{detailLogs.summary.last_error}</p>
                      {detailLogs.summary.last_error_at && (
                        <p className="text-[11px] text-red-500/50 mt-1">{formatTimestamp(detailLogs.summary.last_error_at)}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Log table header with filter */}
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">Execution History</h3>
                  <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/60 border border-border">
                    {(['all', 'success', 'error', 'timeout'] as const).map(f => (
                      <button
                        key={f}
                        onClick={() => setLogFilter(f)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                          logFilter === f
                            ? 'bg-background text-foreground shadow-sm border border-border'
                            : 'text-muted-foreground hover:text-foreground border border-transparent'
                        }`}
                      >
                        {f === 'all' ? 'All' : f === 'success' ? 'Success' : f === 'error' ? 'Errors' : 'Timeouts'}
                        {f !== 'all' && detailLogs && (
                          <span className={`ml-1 text-[10px] ${logFilter === f ? 'opacity-70' : 'opacity-50'}`}>
                            {detailLogs.logs.filter(l => l.status === f).length}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Log table */}
                {filteredLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-border rounded-xl bg-muted/20">
                    <div className="p-3 rounded-full bg-muted mb-3">
                      <Activity className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-foreground">No logs found</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {logFilter === 'all' ? 'Run this skill to see execution logs here.' : `No ${logFilter} logs recorded.`}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-xl border border-border overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-muted/30">
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Timestamp</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Script</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Latency</th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Result</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filteredLogs.map((log) => (
                          <React.Fragment key={log.id}>
                          <tr className="transition-colors hover:bg-muted/20">
                            <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap font-mono">{formatTimestamp(log.created_at)}</td>
                            <td className="px-4 py-3">
                              {log.status === 'success' && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                  <CheckCircle className="w-3 h-3" />Success
                                </span>
                              )}
                              {log.status === 'error' && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-600 dark:text-red-400">
                                  <AlertTriangle className="w-3 h-3" />Error
                                </span>
                              )}
                              {log.status === 'timeout' && (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-500/10 text-orange-600 dark:text-orange-400">
                                  <Timer className="w-3 h-3" />Timeout
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-foreground font-medium">{log.script_name}</td>
                            <td className="px-4 py-3 text-right">
                              {log.latency_ms != null ? (
                                <span className={`text-xs font-mono font-medium ${
                                  log.latency_ms < 500 ? 'text-emerald-600 dark:text-emerald-400' :
                                  log.latency_ms < 2000 ? 'text-orange-600 dark:text-orange-400' :
                                  'text-red-600 dark:text-red-400'
                                }`}>{formatLatency(log.latency_ms)}</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {log.error_message ? (
                                <button
                                  onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                                  className={`inline-flex items-center gap-1.5 text-xs transition-colors ${
                                    expandedLogId === log.id ? 'text-red-500' : 'text-red-500/70 hover:text-red-500'
                                  }`}
                                >
                                  <ArrowRight className={`w-3 h-3 transition-transform ${expandedLogId === log.id ? 'rotate-90' : ''}`} />
                                  View error
                                </button>
                              ) : (
                                <span className="text-xs text-muted-foreground">{log.result_preview || 'Completed'}</span>
                              )}
                            </td>
                          </tr>
                          {expandedLogId === log.id && log.error_message && (
                            <tr>
                              <td colSpan={5} className="px-4 pb-3 pt-0">
                                <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3 max-h-48 overflow-y-auto">
                                  <p className="text-[11px] font-semibold uppercase tracking-wider mb-2 text-red-500">Error Details</p>
                                  <pre className="text-xs whitespace-pre-wrap font-mono leading-relaxed text-red-400/80">{log.error_message}</pre>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Error state */}
            {!isLoadingLogs && !detailLogs && (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="p-3 rounded-full bg-red-500/10">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <p className="text-sm font-medium text-foreground">Failed to load logs</p>
                <p className="text-xs text-muted-foreground">Check your connection and try again.</p>
                <Button variant="outline" size="sm" onClick={() => handleOpenDetail(detailSkill)}>Retry</Button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Skill Builder — conditionally rendered + key forces fresh mount per skill */}
      {showBuilder && (
        <SkillBuilder
          key={builderSkillId || 'create'}
          isOpen={showBuilder}
          onClose={() => { setShowBuilder(false); setBuilderSkillId(null); setEditSkillData(null) }}
          onSaved={loadSkills}
          skillId={builderSkillId}
          initialData={editSkillData}
          chatbotVariables={(chatbot as any).settings?.variables || {}}
        />
      )}

      {/* Config Modal */}
      {showConfigModal && configSkill && (
        <Modal isOpen={showConfigModal} onClose={() => { setShowConfigModal(false); setConfigSkill(null) }} title={`Configure ${configSkill.name}`}>
          <div className="space-y-4">
            {configSkill.has_credentials && (
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">Credentials</h3>
                <Input placeholder="API Key" value={configCredentials['api_key'] || ''} onChange={e => setConfigCredentials({ ...configCredentials, api_key: e.target.value })} />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowConfigModal(false); setConfigSkill(null) }}>Cancel</Button>
              <Button onClick={handleSaveConfig}>Save & Enable</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <Modal isOpen={showUploadModal} onClose={() => { setShowUploadModal(false); setUploadFile(null) }} title="Upload Skill">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Upload a .zip containing a SKILL.md file.</p>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input type="file" accept=".zip" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary" />
              {uploadFile && <p className="mt-2 text-sm text-green-600">{uploadFile.name}</p>}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowUploadModal(false); setUploadFile(null) }}>Cancel</Button>
              <Button onClick={handleUpload} disabled={!uploadFile || isUploading}>
                {isUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Upload
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default SkillsTab
